import { useEffect, useRef, useState } from 'react';
import { getRentalHistoryByIdUsingJob } from '../services/rentalHistory';
import { format } from 'date-fns';
import { api } from '..';

// Global caches to prevent duplicate jobs
const jobCache = new Map<string, string>(); // Maps data ID → job ID
const pendingJobs = new Set<string>(); // Tracks jobs in progress
const resultCache = new Map<string, any>(); // Stores completed results

const formatDate = (d: Date) => format(d, 'yyyy-MM-dd');
let intervalId: number | null = null;

export const useRentalHistoryJob = <T>(
  id: string,
  startDate: Date,
  endDate: Date,
  enabled: boolean,
  pollingInterval: number = 2000,
) => {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const jobIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !id) return;

    // Check if result for id is found on cache
    if (resultCache.has(id)) {
      setIsSuccess(true);
      setData(resultCache.get(id));
      return;
    }

    // Reset jobIdRef when id changes so a new job can start
    jobIdRef.current = jobCache.get(id) || null;

    let localJobId: string | null = jobCache.get(id) || null;

    // Starts a job on server
    const startJob = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if a job already exists for id
        if (!localJobId && !pendingJobs.has(id)) {
          // Mark id as pending
          pendingJobs.add(id);

          // Start a new job
          const data = await getRentalHistoryByIdUsingJob(
            id,
            formatDate(startDate),
            formatDate(endDate),
          );

          if (!data.jobId) throw new Error('Failed to start job.');

          localJobId = data.jobId; // Store job ID in local variable
          if (localJobId) {
            jobCache.set(id, localJobId); // Cache the job ID
          }
          jobIdRef.current = localJobId;

          // Remove pending flag
          pendingJobs.delete(id);
        } else {
          console.log('Job is already running, dont start a new one');
        }

        if (!localJobId) {
          setError('Failed to retrieve job ID.');
          setLoading(false);
          return;
        }

        // Start polling for job status
        const fetchJobStatus = async () => {
          try {
            if (!jobIdRef.current) return;

            const response = await api.get(
              `/jobs/${jobIdRef.current}`,
            );
            const jobData = response.data;

            if (jobData.status === 'completed') {
              setData(jobData.result || null);
              resultCache.set(id, jobData.result); // Cache result
              setLoading(false);
              if (intervalId) {
                clearInterval(intervalId);
              }
              setIsSuccess(true);
              jobCache.delete(id); // Remove job from cache
            } else if (jobData.status === 'failed') {
              setError(jobData.error || 'Job failed.');
              setLoading(false);
              if (intervalId) {
                clearInterval(intervalId);
              }
              jobCache.delete(id);
            }
          } catch (err) {
            console.error('Error fetching job status:', err);
            setError('Error fetching job status.');
            setLoading(false);
            if (intervalId) {
              clearInterval(intervalId);
            }
          }
        };
        // Always clear previous before starting a new one
        if (intervalId) {
          clearInterval(intervalId);
        }
        intervalId = setInterval(fetchJobStatus, pollingInterval);
        fetchJobStatus(); // Initial fetch
      } catch (err) {
        setError((err as Error).message);
        setLoading(false);
      }
    };

    startJob();

    return () => {
      console.log('Cleanup: Clearing polling interval');
      if (intervalId) {
        clearInterval(intervalId);
      }
    }; // Cleanup polling on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return { data, error, loading, isSuccess };
};
